import * as THREE from 'three';
import { Evaluator, Brush, SUBTRACTION, ADDITION, INTERSECTION } from 'three-bvh-csg';
import { MeshBVH } from 'three-mesh-bvh';
import { createLoftGeometry, createSweptBlendGeometry } from '../geometry/LoftGeometry';

/**
 * Solid Geometry Kernel - BIM 2025 Grade
 * 
 * Responsible for high-precision boolean operations on 3D meshes.
 * Powered by 'three-bvh-csg' for robust constructive solid geometry.
 */
export class SolidKernel {

    // Re-use a single evaluator instance for performance
    private static evaluator = new Evaluator();

    /**
     * Perform a Union operation (A + B)
     */
    static union(meshA: THREE.Mesh, meshB: THREE.Mesh): THREE.Mesh {
        return this.performOperation(meshA, meshB, ADDITION);
    }

    /**
     * Perform a Subtract operation (A - B)
     */
    static subtract(meshA: THREE.Mesh, meshB: THREE.Mesh): THREE.Mesh {
        return this.performOperation(meshA, meshB, SUBTRACTION);
    }

    /**
     * Perform an Intersect operation (A & B)
     */
    static intersect(meshA: THREE.Mesh, meshB: THREE.Mesh): THREE.Mesh {
        return this.performOperation(meshA, meshB, INTERSECTION);
    }

    private static performOperation(meshA: THREE.Mesh, meshB: THREE.Mesh, op: any): THREE.Mesh {
        // Ensure matrices are updated
        meshA.updateMatrixWorld(true);
        meshB.updateMatrixWorld(true);

        // Create Brushes from meshes
        // Note: Brush clones the geometry, so we are safe from mutating originals
        const brushA = new Brush(meshA.geometry, meshA.material as THREE.Material);
        const brushB = new Brush(meshB.geometry, meshB.material as THREE.Material);

        // Sync transforms
        brushA.applyMatrix4(meshA.matrixWorld);
        brushB.applyMatrix4(meshB.matrixWorld);

        // Ensure BVH is built (Evaluator does this, but explicit update helps debugging)
        brushA.updateMatrixWorld(true);
        brushB.updateMatrixWorld(true);

        // Evaluate
        // useGroups: true preserves materials
        this.evaluator.useGroups = true;

        // Consolidate materials to prevent fragmentation in complex joins
        this.evaluator.consolidateMaterials = true;

        const result = this.evaluator.evaluate(brushA, brushB, op);

        // Reset transform dependent on the operation result context
        // The result is in World Space. We often want it in Local Space of A?
        // Usually, we want the result to replace A.
        // A was at position P.
        // Result is at (0,0,0) world, but vertices are transformed.

        // For simple replacement, we return the world-space mesh.
        // The consumer should handle parenting.

        // Cast shadow / Receive shadow transfer
        result.castShadow = meshA.castShadow;
        result.receiveShadow = meshA.receiveShadow;

        return result;
    }


    /**
     * Create a simple linear extrusion
     */
    static createExtrusion(shape: THREE.Shape, depth: number): THREE.Mesh {
        const geometry = new THREE.ExtrudeGeometry(shape, {
            depth: depth,
            bevelEnabled: false,
            curveSegments: 24
        });
        const mesh = new THREE.Mesh(geometry);
        mesh.updateMatrix();
        return mesh;
    }

    /**
     * Create a revolved solid (Lathe)
     * @param points - Points of the profile to revolve (Vector2)
     * @param axis - Axis of revolution (for now assumes Y axis, generic axis requires rotation)
     * @param angle - Angle of revolution (default 2PI)
     */
    static createRevolve(points: THREE.Vector2[], angle: number = Math.PI * 2): THREE.Mesh {
        const geometry = new THREE.LatheGeometry(points, 24, 0, angle);
        // Lathe revolves around Y axis.
        const mesh = new THREE.Mesh(geometry);
        mesh.updateMatrix();
        return mesh;
    }

    /**
     * Create a blend (Loft) between two profiles — a real ruled solid with
     * side band and triangulated caps (shared LoftGeometry implementation).
     */
    static createBlend(bottomProfile: THREE.Shape, topProfile: THREE.Shape, height: number): THREE.Mesh {
        const geometry = createLoftGeometry(bottomProfile, topProfile, height);
        const mesh = new THREE.Mesh(geometry);
        mesh.updateMatrix();
        return mesh;
    }

    /**
     * Create a swept blend (profile morphing start → end along the path).
     */
    static createSweptBlend(startProfile: THREE.Shape, endProfile: THREE.Shape, path: THREE.CurvePath<THREE.Vector3>): THREE.Mesh {
        const geometry = createSweptBlendGeometry(startProfile, endProfile, path);
        const mesh = new THREE.Mesh(geometry);
        mesh.updateMatrix();
        return mesh;
    }

    /**
     * Create a solid from a profile and path (Sweep)
     * Useful for Wall Sweeps, Railings, etc.
     */
    static createSweep(shape: THREE.Shape, path: THREE.CurvePath<THREE.Vector3>, segments: number = 20): THREE.Mesh {
        const geometry = new THREE.ExtrudeGeometry(shape, {
            extrudePath: path,
            steps: segments,
            bevelEnabled: false
        });
        const mesh = new THREE.Mesh(geometry);
        mesh.updateMatrix();
        return mesh;
    }

    /**
     * Check if two meshes actually intersect: AABB early-out, then a
     * precise triangle-level overlap test through three-mesh-bvh so the
     * result never confuses a near miss with a "Potential Join".
     */
    static intersects(meshA: THREE.Mesh, meshB: THREE.Mesh): boolean {
        meshA.updateMatrixWorld(true);
        meshB.updateMatrixWorld(true);

        // 1. Fast Bounding Box Check
        const boxA = new THREE.Box3().setFromObject(meshA);
        const boxB = new THREE.Box3().setFromObject(meshB);
        if (!boxA.intersectsBox(boxB)) {
            return false;
        }

        // 2. Precise triangle-level overlap (BVH accelerated).
        const bvhA = new MeshBVH(meshA.geometry);
        const bToA = meshA.matrixWorld.clone().invert().multiply(meshB.matrixWorld);
        return bvhA.intersectsGeometry(meshB.geometry, bToA);
    }

    /**
     * Apply a list of voids to a solid sequentially.
     */
    static applyVoids(solid: THREE.Mesh, voids: THREE.Mesh[]): THREE.Mesh {
        let result = solid;
        for (const voidMesh of voids) {
            // Check intersection first for performance
            if (this.intersects(result, voidMesh)) {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const _previous = result;
                result = this.subtract(result, voidMesh);
                // Clean up previous mesh if it wasn't the original?
                // In Three.js, geometry memory management is manual.
                // For now, we rely on GC or explicit dispose elsewhere.
            }
        }
        return result;
    }
}
